import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CLOSED_LOST_STAGE_CODE, NOT_PROCEEDING_REASON_OTHER, SalesFolderStatus } from '@gh/builder-shared-data';
import { currentNormalizedDate } from '@gh/core-util';

export const NOT_PROCEEDING_NO_CONTACT = '8';
export const NURTURE_ELIGIBLE_REASON_CODES = ['1', '4', '5', '6'];
export const NOT_PROCEEDING_OTHER_MAX_LENGTH = 200;
export const MSG_NOT_PROCEEDING_REASON_REQUIRED = 'msg_not_proceeding_reason_required';
export const MSG_NOT_PROCEEDING_REASON_OTHER_REQUIRED = 'msg_not_proceeding_reason_other_required';
export const MSG_RE_ENGAGE_DATE_IN_PAST = 'msg_re_engage_date_in_past';

export const LOST_LEAD_META_REASON = 'reason';
export const LOST_LEAD_META_REASON_OTHER = 'reason_other';
export const LOST_LEAD_META_RE_ENGAGE_DATE = 're_engage_date';

export type LostLeadMetadataInput = {
  notProceedingReason?: string | number | null;
  notProceedingReasonOther?: string | null;
  reEngageDate?: Date | string | null;
};

export type WillNotCompleteTriggerInput = LostLeadMetadataInput & {
  status?: string | number | null;
  /** Saved stage code; a Will Not Complete lead that is not in Closed Lost yet is re-evaluated. */
  stage?: string | number | null;
  previousStatus?: string | number | null;
  previousNotProceedingReason?: string | number | null;
  previousNotProceedingReasonOther?: string | null;
  previousReEngageDate?: Date | string | null;
};

export type NotProceedingFieldVisibility = {
  reasonVisible: boolean;
  otherVisible: boolean;
  reEngageVisible: boolean;
};

export const isOtherNotProceedingReason = (code?: string | number | null): boolean =>
  `${code}` === NOT_PROCEEDING_REASON_OTHER;

export const isRequestedNoContactReason = (code?: string | number | null): boolean =>
  `${code}` === NOT_PROCEEDING_NO_CONTACT;

export const isNurtureEligibleReason = (code?: string | number | null): boolean =>
  code != null && code !== '' && NURTURE_ELIGIBLE_REASON_CODES.includes(`${code}`);

export const isWillNotCompleteStatus = (status?: string | number | null): boolean => {
  if (status == null || status === '') {
    return false;
  }
  if (status === SalesFolderStatus.WillNotComplete) {
    return true;
  }
  const asNumber = Number(status);
  if (!Number.isNaN(asNumber) && asNumber === 3) {
    return true;
  }
  const normalized = `${status}`.toUpperCase().replace(/[\s-]/g, '_');
  return normalized === 'WILL_NOT_COMPLETE' || normalized === 'WILLNOTCOMPLETE';
};

export const hasOtherReasonText = (value?: string | null): boolean =>
  !!(value && value.trim());

const isBlank = (value: unknown): boolean =>
  value == null || value === '' || (typeof value === 'string' && !value.trim());

export const requiredNotProceedingReason: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  isBlank(control.value) ? { notProceedingReason: { messageId: MSG_NOT_PROCEEDING_REASON_REQUIRED } } : null;

export const requiredNotProceedingReasonOther: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  isBlank(control.value) ? { notProceedingReasonOther: { messageId: MSG_NOT_PROCEEDING_REASON_OTHER_REQUIRED } } : null;

/** True when `value` is a day before `today` (both compared as calendar days). */
export const isReEngageDateInPast = (value: Date | string | null | undefined, today: Date): boolean => {
  const day = toIsoDate(value);
  return !!day && day < toIsoDate(today);
};

/**
 * A re-engage date the user picks may not be in the past: the nurture task would be overdue at once.
 * Only an edited (dirty) value is checked, so a stored date that has since passed does not block other edits.
 * The backend applies the same rule (LostLeadFields.assertReEngageDateNotInPast).
 */
export const reEngageDateNotInPast: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  control.dirty && isReEngageDateInPast(control.value, currentNormalizedDate())
    ? { reEngageDate: { messageId: MSG_RE_ENGAGE_DATE_IN_PAST } }
    : null;

export const toIsoDate = (value?: Date | string | null): string => {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toLostLeadMetadata = (input: LostLeadMetadataInput): { [key: string]: string } => {
  const metadata: { [key: string]: string } = {};
  if (input.notProceedingReason != null && input.notProceedingReason !== '') {
    metadata[LOST_LEAD_META_REASON] = `${input.notProceedingReason}`;
  }
  const other = (input.notProceedingReasonOther || '').trim();
  if (other && isOtherNotProceedingReason(input.notProceedingReason)) {
    metadata[LOST_LEAD_META_REASON_OTHER] = other;
  }
  if (isNurtureEligibleReason(input.notProceedingReason)) {
    const date = toIsoDate(input.reEngageDate);
    if (date) {
      metadata[LOST_LEAD_META_RE_ENGAGE_DATE] = date;
    }
  }
  return metadata;
};

export const shouldEvaluateWillNotComplete = (input: WillNotCompleteTriggerInput): boolean => {
  if (!isWillNotCompleteStatus(input.status)) {
    return false;
  }
  if (!isWillNotCompleteStatus(input.previousStatus)) {
    return true;
  }
  // An earlier evaluate failed (or the tab closed) after the save: the lead is stuck outside Closed Lost.
  if (input.stage != null && input.stage !== '' && `${input.stage}` !== `${CLOSED_LOST_STAGE_CODE}`) {
    return true;
  }
  return `${input.notProceedingReason ?? ''}` !== `${input.previousNotProceedingReason ?? ''}`
    || (input.notProceedingReasonOther || '').trim() !== (input.previousNotProceedingReasonOther || '').trim()
    || toIsoDate(input.reEngageDate) !== toIsoDate(input.previousReEngageDate);
};

export const notProceedingFieldVisibility = (
  status?: string | number | null,
  reason?: string | number | null,
  assumeWillNotComplete = false,
): NotProceedingFieldVisibility => {
  const willNotComplete = assumeWillNotComplete || isWillNotCompleteStatus(status);
  return {
    reasonVisible: willNotComplete,
    otherVisible: willNotComplete && isOtherNotProceedingReason(reason),
    reEngageVisible: willNotComplete && isNurtureEligibleReason(reason),
  };
};
